import React from 'react';
import { Alert, Modal, ModalProps } from 'react-bootstrap';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { RepresentativeForm } from '../Forms/RepresentativeForm';
import { ControlledModal } from '../../../common/ControlledModal';
import { Representative } from '../../types';
import { updateRepresentativeInformation } from '../backofficeServices';
import { representativeSchema, RepresentativeSchema } from '../schemas';
import { config } from '../config';

const GENERIC_ERROR_MESSAGE = 'Ocurrió un error al actualizar la información del representante';

export function UpdateRepresentativeInformationModal({
  show, onHide, data,
}: ModalProps & { data: Representative }) {
  const form = useForm<Pick<RepresentativeSchema, 'representative_email'>>({
    resolver: zodResolver(representativeSchema.pick({ representative_email: true })),
    defaultValues: {
      representative_email: data.representative_email,
    },
  });

  const queryClient = useQueryClient();

  const {
    mutate, isLoading, isError, reset,
  } = useMutation({
    mutationFn: (input: Pick<RepresentativeSchema, 'representative_email'>) => updateRepresentativeInformation(data.id, input),
    onSuccess: async () => {
      await queryClient.invalidateQueries(
        { queryKey: [config.tanstack.baseSigningProcessesQueryKey] });
      form.reset();
      onHide?.();
    },
  });

  const handleClose = () => {
    form.reset({ representative_email: data.representative_email });
    reset();
    onHide?.();
  };
  return (
    <ControlledModal
      title="Actualizar información del representante"
      closeButton
      show={show}
      onHide={handleClose}
      centered
    >
      <Modal.Body>
        <RepresentativeForm form={form} onSubmit={mutate}>
          {isError && (
            <Alert variant="danger" title="Error">
              {GENERIC_ERROR_MESSAGE}
            </Alert>
          )}
          <ControlledModal.Actions
            handleClose={handleClose}
            isPending={isLoading}
            cancelButton
            submitButtonProps={{
              disabled: !form.formState.isDirty || isLoading,
              type: 'submit',
            }}
          />
        </RepresentativeForm>
      </Modal.Body>
    </ControlledModal>
  );
}
